import type { ButtonHTMLAttributes, ReactElement } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-accent-500 hover:bg-accent-600 active:bg-accent-700 text-stone-950 ' +
    'shadow-[0_6px_20px_-6px_rgb(212_136_28_/_0.55)] hover:shadow-[0_8px_24px_-6px_rgb(212_136_28_/_0.65)] ' +
    'font-semibold',
  secondary:
    'bg-surface-elevated hover:bg-surface-panel text-ink ' +
    'border border-surface-border hover:border-surface-strong',
  ghost:
    'bg-transparent hover:bg-surface-panel text-ink-muted hover:text-ink',
  danger:
    'bg-transparent hover:bg-loss-DEFAULT/10 text-loss-DEFAULT ' +
    'border border-loss-DEFAULT/30 hover:border-loss-DEFAULT/60',
};

const SIZE: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  className,
  children,
  ...rest
}: Props): ReactElement {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
        'transition-all duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
          <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {children}
    </button>
  );
}
